import { getCurrentScope, onScopeDispose } from 'vue'

/**
 * Composable for handling the `pageshow` event.
 *
 * Cleanup is registered against the current `EffectScope` when one is active (inside a component, composable, or Pinia store). When called outside any scope (e.g. from `main.ts`), the listener lives for the lifetime of the page, which is the desired behavior for app-global listeners.
 *
 * @param callback - Function invoked on every `pageshow` event.
 */
function usePageShow(callback: (event: PageTransitionEvent) => void) {
  const handler = (event: Event) => callback(event as PageTransitionEvent)

  window.addEventListener('pageshow', handler)

  if (getCurrentScope()) {
    onScopeDispose(() => window.removeEventListener('pageshow', handler))
  }
}

/**
 * Composable for handling the `pageshow` event, including bfcache (back/forward cache) restores.
 *
 * When a page is restored from the bfcache (e.g. user navigates back), component setup and `onMounted` hooks do NOT re-run, so a `pageshow` listener is the only way to react to the page becoming visible again. `event.persisted` is `true` for bfcache restores.
 *
 * @param callback - Function invoked on every `pageshow` event, when the event has the `persisted` flag (e.g. bfcache restores).
 */
export function usePageShowPersisted(callback: (event: PageTransitionEvent) => void) {
  usePageShow((event) => {
    if (event.persisted) {
      callback(event)
    }
  })
}
